<script setup lang="ts">
import { useCheckoutStore } from '@/stores/checkout.ts';
import { computed } from 'vue';
import { $t } from '@/composables/translations/useTranslate.ts';
import { HIDE_AUXILIARIES_STEP } from '@/constants/checkout/config.ts';

const allSteps = [
  { stepIndex: 0, label: $t('ibe.checkout.step-1', 'Your Vamos journey') },
  { stepIndex: 1, label: $t('ibe.checkout.step-2', 'Your data') },
  { stepIndex: 2, label: $t('ibe.checkout.step-3', 'Verification and payment') },
];

const steps = computed(() =>
  HIDE_AUXILIARIES_STEP ? allSteps.filter((s) => s.stepIndex !== 0) : allSteps
);

const checkout = useCheckoutStore();

const finishedStep = computed(() => {
  return (stepIndex: number) => {
    if (stepIndex < checkout.activeStep) {
      return 'w-full border-actions-primary';
    }
    if (stepIndex === checkout.activeStep - 1) {
      return 'w-full border-actions-primary';
    }
    return 'w-0 opacity-0';
  };
});

const connectorPosition = (position: number) => {
  if (steps.value.length === 2) {
    return 'left-1/2 -translate-x-1/2';
  }
  return position === 0 ? 'ml-32' : 'ml-8 left-1/2';
};
</script>

<template>
  <div class="max-w-xl relative w-full mb-7.5">
    <div class="flex lg:items-center">
      <template v-for="(step, position) in steps" :key="step.stepIndex">
        <div class="flex flex-col items-center w-full">
          <button
            class="w-8 h-8 text-center duration-500 border rounded-full text-body-large hover:opacity-50"
            :class="{
              'border-actions-primary': step.stepIndex === checkout.activeStep,
              'opacity-50 border-black border-opacity-50': step.stepIndex > checkout.activeStep,
              'bg-actions-primary border-actions-primary': step.stepIndex < checkout.activeStep,
            }"
            @click="step.stepIndex <= checkout.activeStep && checkout.setActiveStep(step.stepIndex)"
          >
            <i
              v-if="step.stepIndex < checkout.activeStep"
              class="w-6 h-3 bg-white icon icon-checkmark"
            ></i>
            {{ step.stepIndex < checkout.activeStep ? '' : position + 1 }}
          </button>
          <span
            class="pt-2 text-center text-body-small"
            :class="{
              'opacity-50': step.stepIndex > checkout.activeStep,
            }"
          >
            {{ step.label }}
          </span>
        </div>

        <div
          v-if="position < steps.length - 1"
          :class="[
            'max-w-32 w-full absolute border-2 lg:flex hidden z-10 border-black border-opacity-50 border-t mb-7 rounded-full',
            connectorPosition(position),
          ]"
        ></div>
        <div
          v-if="position < steps.length - 1"
          :class="[
            'max-w-32 absolute duration-300 lg:flex hidden z-20 border-t border-2 mb-7 rounded-full ',
            connectorPosition(position),
            finishedStep(step.stepIndex),
          ]"
        ></div>
      </template>
    </div>
  </div>
</template>
